import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { AutomataOperationsService, Automation, AutomationInterface, Context, EntityType } from '@vendasta/automata';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { ManualAutomationService } from '../manual-automation.service';
import { ListItemsApiService, StartManualAutomationsRequest } from '@vendasta/bulk-actions';

const PERMISSION_DENIED = 7;

@Component({
  selector: 'automata-start-manual-automation-form',
  templateUrl: './start-manual-automation-form.component.html',
  styleUrls: ['./start-manual-automation-form.component.scss'],
  providers: [ManualAutomationService],
})
export class StartManualAutomationFormComponent implements OnInit, OnDestroy {
  manualAutomationSearch: FormControl<string> = new FormControl<string>('');
  automations$: Observable<Automation[]> = this.manualAutomationService.automations$;
  hasMore$: Observable<boolean> = this.manualAutomationService.hasMore$;
  loading$: Observable<boolean> = this.manualAutomationService.loading$;
  hasManualAutomations$: Observable<boolean>;
  searchTerm = '';
  selectedAutomation: Automation;
  starting: boolean;

  @Input() title: string;
  @Input() companyName: string;
  @Input() namespace: string;
  @Input() actionListName: string;
  @Input() hideSettingsLink = false;
  @Input() onlySalespersonAutomations = false;
  @Input() isListOfEntities = false; // used for lists so that separate endpoint can be used (uses row id)
  // Extra warnings to display to the user
  @Input() warnings: string[] = [];
  @Input() showMarket = false;
  // Entity type should only be used for list actions or if you don't want to start an automation.
  // If you want the automation to start with an actual entity use entities instead.
  @Input() entityType: EntityType;
  @Input() entities: Map<EntityType, string[]>;
  @Input() onlySelectDontStart = false;
  @Input() hasAllMarketAccess = false;
  @Input() context: Context;

  @Output() cancel = new EventEmitter();
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() success = new EventEmitter<AutomationInterface>();

  private destroyed$$ = new Subject<void>();

  entityTypes: EntityType[] = [];

  constructor(
    private readonly manualAutomationService: ManualAutomationService,
    private readonly listItemService: ListItemsApiService,
    private readonly cdr: ChangeDetectorRef,
    private readonly router: Router,
    private readonly operationsService: AutomataOperationsService,
    private readonly alertService: SnackbarService,
  ) {}

  ngOnInit(): void {
    this.entityTypes = this.entities ? Array.from(this.entities.keys()) : [];
    if (this.entityType) {
      this.entityTypes.push(this.entityType);
    }

    this.manualAutomationService.init(
      this.searchTerm,
      this.namespace,
      this.onlySalespersonAutomations,
      this.entityTypes,
      this.context,
      15,
    );

    this.hasManualAutomations$ = this.manualAutomationService.automations$.pipe(
      take(1),
      map((automations) => automations?.length > 0),
    );
  }

  ngOnDestroy(): void {
    this.destroyed$$.next();
    this.destroyed$$.unsubscribe();
  }

  goToSettings(): void {
    this.router.navigate(['automations']);
    this.cancel.emit();
  }

  start(): void {
    if (!this.selectedAutomation) {
      return;
    }
    if (this.onlySelectDontStart) {
      this.success.emit(this.selectedAutomation);
      return;
    }
    const payloads = (this.entities?.get(this.selectedAutomation.entityType) || []).map((id) => ({ entityId: id }));
    this.starting = true;

    const startAccountGroup$ = this.operationsService.startAccountGroupAutomation(
      this.selectedAutomation.id,
      [],
      this.actionListName,
      this.namespace,
    );

    const startEntities$ = this.operationsService.startAutomation(
      this.selectedAutomation.id,
      undefined,
      undefined,
      payloads,
      this.namespace,
    );

    const listRequest: StartManualAutomationsRequest = {
      namespace: this.namespace,
      listId: payloads?.length > 0 ? payloads[0]?.entityId : '',
      automationId: this.selectedAutomation.id,
    } as StartManualAutomationsRequest;
    const startList$ = this.listItemService.startManualAutomations(listRequest).pipe(map(() => true));

    const start$ = this.isListOfEntities ? startList$ : this.actionListName ? startAccountGroup$ : startEntities$;
    start$.pipe(take(1)).subscribe({
      next: () => {
        this.starting = false;
        this.cdr.detectChanges();
        this.alertService.openSuccessSnack('AUTOMATIONS.MANUAL_AUTOMATIONS.AUTOMATION_STARTED', {
          interpolateTranslateParams: {
            name: this.selectedAutomation.name,
          },
        });
        this.success.emit(this.selectedAutomation);
      },
      error: (err) => {
        console.error(err);
        this.starting = false;
        this.cdr.detectChanges();
        if (err.error.code === PERMISSION_DENIED) {
          this.alertService.openErrorSnack('AUTOMATIONS.EDITOR.ERRORS.PERMISSION_DENIED_STARTING_AUTOMATION');
        } else {
          this.alertService.openErrorSnack('AUTOMATIONS.EDITOR.ERRORS.ERROR_STARTING_AUTOMATION');
        }
        this.cancel.emit();
      },
    });
  }

  updateSearchTerm(event: Event): void {
    this.selectedAutomation = null;
    const searchValue = (event.target as HTMLInputElement).value;
    this.manualAutomationService.updateSearchTerm(searchValue);
  }

  displayFn(automation?: Automation): string | undefined {
    return automation ? automation.name : undefined;
  }

  fetchNextPage(): void {
    this.manualAutomationService.fetchNextPage();
  }

  emitClose(): void {
    this.cancel.emit();
  }

  /** Causes the form to load more automations if the end of the list has been seen. */
  loadMoreAutomations(): void {
    combineLatest([this.loading$, this.hasMore$])
      .pipe(take(1))
      .subscribe(([loading, hasMore]) => {
        if (!loading && hasMore) {
          this.fetchNextPage();
        }
      });
  }
}
